import { useEffect, type ReactNode } from 'react';
import { type FlightExplorerOffer, type FlightSearchParams } from './search_source.js';

import { useExplorerEnv, type FlightExplorerEnv } from './explorer_env.js';

import { fmtMoneyWhole } from '../results/flight_format.js';
import { activeAxes } from '../results/filter_logic.js';
import { filterEmptyHint } from './empty_hint.js';
import {
  OfferListBody,
  type FlightPinControl,
  type OfferFares,
  type OfferListEnv,
} from '../results/offer/offer_list.js';

import {
  activeOffers,
  explorerBusy,
  explorerRefreshing,
  legSearchParams,
  searchingLeg,
  type ExplorerState,
} from './explorer_state.js';
import { faresTextOf, type SelectFaresFn } from './explorer_fares_stage.js';
import type { FlightExplorerPageCopy } from './explorer_copy.js';
import { ExplorerTopBar, fexRootClass, type FlightExplorerTakeover } from './explorer_top_bar.js';
import type {
  FlightExplorerBandSlot,
  FlightExplorerPillSlot,
  FlightExplorerPlannerSlot,
  FlightExplorerSearchSlots,
} from './explorer_slots.js';

import { ExplorerSkeleton } from './explorer_skeleton.js';

import { rowPropsOf } from './explorer_steps.js';
import { listTitleOf } from './explorer_results.js';
import { SearchEmptyState } from '../../shared/search_empty_state/search_empty_state.js';
import { type SummaryPillTarget } from '../../shared/summary_pill/summary_pill.js';
import { ExplorerError, useSearchEmptyCopy } from './explorer_error.js';

import type { useExplorerIo } from './explorer_io.js';

/*
 * A TELA do explorer: casca, corpo, vazio, rodapé, faixa de busca e camada do
 * planner. Mora fora do `explorer.tsx` para ele caber no teto de linhas antes de
 * mudar de pacote (#4997, C0b); o estado fica no `explorer_io.ts`.
 */

/**
 * A CASCA do explorer, comum às duas telas que ele monta: o chrome do topo, a
 * camada do planner expandido e o corpo inerte enquanto ela está aberta. Sai do
 * corpo do `FlightExplorer` (que vive no teto de complexidade/linhas) e, com
 * isso, a fase 3 herda o MESMO chrome da lista em vez de montar um segundo — a
 * faixa de busca segue sendo o caminho de refazer a viagem inteira nas duas.
 */
export function ExplorerShell({
  state,
  chrome,
  pill,
  trailing,
  takeover,
  planner,
  pillTarget,
  bandLeg,
  io,
  footer,
  onClosePill,
  children,
}: Readonly<{
  state: ExplorerState;
  chrome?: ReactNode;
  pill: ReactNode;
  trailing?: ReactNode;
  takeover?: FlightExplorerTakeover;
  planner?: FlightExplorerSearchSlots['planner'];
  pillTarget: SummaryPillTarget | null;
  bandLeg: number;
  io: ReturnType<typeof useExplorerIo>;
  /** Irmão do corpo, FORA do rolo dos resultados — o rodapé de contagem da
   *  lista. A fase 3 não tem: ele descreve uma lista que saiu de cena. */
  footer?: ReactNode;
  onClosePill: () => void;
  children: ReactNode;
}>) {
  const { hasBar, className } = fexRootClass(chrome, pill, trailing);
  return (
    <div className={className}>
      {hasBar && (
        <ExplorerTopBar
          state={state}
          chrome={chrome}
          pill={pill}
          trailing={trailing}
          takeover={takeover}
        />
      )}
      <PillPlannerLayer
        slot={planner}
        target={pillTarget}
        state={state}
        bandLeg={bandLeg}
        io={io}
        onClose={onClosePill}
      />
      <div className="fex-body row-start-2" inert={pillTarget !== null}>
        {children}
      </div>
      {footer}
    </div>
  );
}

/** Rótulo da TARIFA que fechou (one_way/RT), ou `null` sem perna final fechada. Não repete
 *  o voo (o chip do passo é a casa disso). `fareNames` falta no pick semeado pelo host — caso
 *  do ROTEIRO, que nem renderiza este rodapé; aqui a ausência cai no preço sozinho. */
function farePickLabel(state: ExplorerState): string | null {
  const summary = state.chosen[state.request.mode === 'round_trip' ? 1 : 0]?.summary;
  if (!summary) return null;
  const price = fmtMoneyWhole({ amount: summary.total, currency: summary.currency });
  return summary.fareNames ? `${summary.fareNames} · ${price}` : price;
}

/** Rodapé: a tarifa que fechou + a marca da lista. O Confirmar NÃO mora aqui (é um só, no
 *  chrome do topo — `ExplorerTopBar`). VAZIO não desenha: sem tarifa nem marca, a faixa com
 *  borda e fundo era só uma linha branca por baixo do card do composer. */
export function ExplorerFooter({ state }: Readonly<{ state: ExplorerState }>) {
  const { copy } = useExplorerEnv();
  const fare = state.request.mode === 'multi_leg' ? null : farePickLabel(state);
  const refreshing = explorerRefreshing(state);
  if (!fare && !refreshing) return null;
  return (
    <footer className="fex-foot row-start-3">
      <div className="fex-chips">
        {fare && <span className="fex-chip">{fare}</span>}
        {/* A contagem da SEMENTE é a do card (30, no teto), não a do inventário — e
            a busca viva que a corrige está em voo. Sem esta marca o rodapé afirmava
            um número provisório com a mesma cara do definitivo. `role=status` e não
            um spinner: a lista está utilizável, isto é aviso, não espera. */}
        {refreshing && <output className="fex-refreshing">{copy.page.refreshing}</output>}
      </div>
    </footer>
  );
}

/**
 * A justificativa determinística do card Recomendado (#2108). O fio das buscas
 * do BFF traz os `badges` (dos highlights do modal-session) mas não traz `why`
 * — e é melhor assim: o texto tem de falar o idioma de quem lê, então ele nasce
 * aqui, do i18n, sobre fatos que a própria oferta sustenta (direto / dentro da
 * política). Um `why` que já veio no fio (o card do chat, produtor agents) tem
 * prioridade — este template nunca o sobrescreve.
 */
function withRecommendedWhy(
  rows: FlightExplorerOffer[],
  why: FlightExplorerPageCopy['why'],
): FlightExplorerOffer[] {
  return rows.map((offer) => {
    if (offer.why || !offer.badges?.some((b) => b.kind === 'best')) return offer;
    const direct = (offer.stops ?? offer.segments.length - 1) === 0;
    const inPolicy = offer.policy?.status === 'in';
    const text = direct
      ? inPolicy
        ? why.directInPolicy
        : why.direct
      : inPolicy
        ? why.inPolicy
        : why.recommended;
    return { ...offer, why: text };
  });
}

/** O ambiente da lista de ofertas, no ambiente do explorer: os mesmos textos e
 *  portas, com o detalhe aberto contado na telemetria. */
function offerListEnvOf(env: FlightExplorerEnv): OfferListEnv {
  return {
    text: faresTextOf(env),
    policy: env.copy.policy,
    airportSwitchOf: env.airportSwitchOf,
    renderDetail: env.renderDetail,
    onDetailOpened: () => {
      env.track('drawer_opened', { kind: 'flight_detail' });
    },
  };
}

/**
 * A lista zerou — mas por QUÊ decide a tela (#2339):
 * - vazio-por-FILTRO (algum corte ativo): a copy seca de sempre ("Nenhum voo com
 *   esses filtros") — a saída é afrouxar o corte, e o painel segue em cena.
 * - zero REAL do fornecedor (NENHUM corte ativo): a empty page — a busca
 *   respondeu "não há", e a saída é buscar de novo ou refazer a busca.
 *
 * `activeAxes` é o mesmo predicado do "Redefinir": não-vazio ⇒ há corte, e a dica
 * nomeia os eixos que cortam (#3239 C5). O
 * `searchCeiling` é o que separa o corte de preço-que-zera do zero cru — sem ele,
 * um `maxPrice` abaixo do teto do CONJUNTO (mas a lista já veio vazia do refine)
 * não contaria como eixo, e o filtro passaria por zero real.
 *
 * A falha de upstream NÃO cai aqui: `leg.phase === 'error'` é tratado antes, no
 * `ExplorerBody` — a MESMA página, em tom `error` (título como `alert`).
 *
 * CONGELADO (`frozen`/readOnly): a empty page fica SEM ações — o explorer readOnly
 * não pode nem re-buscar nem sair, exatamente como o ramo de erro. Só o título
 * permanece.
 *
 * Extraído do `ExplorerBody` pelo teto de complexidade da view.
 */
function ExplorerEmpty({
  state,
  frozen,
  refining,
  searchCeiling,
  onRetry,
  onNewSearch,
}: Readonly<{
  state: ExplorerState;
  frozen: boolean;
  refining: boolean;
  searchCeiling?: number;
  onRetry: () => void;
  onNewSearch?: () => void;
}>) {
  const { copy: text, locale, places } = useExplorerEnv();
  const copy = useSearchEmptyCopy();
  const axes = activeAxes(activeOffers(state), state.filter, searchCeiling);
  const hint = filterEmptyHint(axes, text.page, text.filter);
  // A lista vazia com o refine em voo ainda não é resposta: o corte do cliente
  // esvaziou a página em cena (ou a anterior veio vazia) e a do servidor está a
  // caminho. O vazio aqui dizia "Não encontramos voos" por ~10s sobre uma busca
  // que tem voos (#3844).
  if (refining) return <ExplorerSkeleton searchKey={`refine:${String(state.activeLeg)}`} />;
  if (axes.length > 0) {
    return (
      <div className="fex-empty">
        <p>{text.page.emptyFilterTitle}</p>
        <p className="fex-empty-hint" role="status">
          {hint}
        </p>
      </div>
    );
  }
  return (
    <SearchEmptyState
      tone="empty"
      title={text.page.emptyResults(listTitleOf(state, locale, places.cityOf)?.city ?? '')}
      copy={copy}
      {...(!frozen && onNewSearch ? { onNewSearch } : {})}
      {...(frozen ? {} : { onRetry })}
    />
  );
}

/** O seq da busca em voo da perna ativa — a identidade que faz a contagem da
 *  espera recomeçar a cada busca nova (e só aí). */
const activeSeq = (state: ExplorerState): number => state.seqs[state.activeLeg] ?? 0;

/** O corpo da fase ativa: loading/erro/vazio ou a lista de ofertas — a MESMA
 *  `OfferListBody` do card do chat nas duas fases do RT (select do VOO na 1,
 *  escada do PAR na 2) e no one_way/multi. */
export function ExplorerBody({
  state,
  rows,
  frozen,
  fares,
  searchCeiling,
  refining,
  onRetry,
  onNewSearch,
  onSearch,
  onPickFlight,
  onSelectFares,
  pin,
}: Readonly<{
  state: ExplorerState;
  rows: FlightExplorerOffer[];
  frozen: boolean;
  fares: OfferFares<FlightExplorerOffer>;
  /** Refine em voo (a mesma busca, refiltrada no servidor) — ver o vazio abaixo. */
  refining: boolean;
  /** Fixar/desfixar por linha (#2294) — repasse cru ao `OfferListBody`. Ausente
   *  ⇒ sem toggle (a casca do chat, ou o `readOnly` do checkout). */
  pin?: FlightPinControl<FlightExplorerOffer>;
  /** Teto de preço do CONJUNTO — distingue o vazio-por-filtro do zero real (ver
   *  `ExplorerEmpty`). */
  searchCeiling?: number;
  onRetry: () => void;
  onNewSearch?: () => void;
  /** Re-forma o pedido com outro aeroporto (atalho do prazo estourado, #4465). */
  onSearch: (params: FlightSearchParams) => void;
  /** A linha escolhe o VOO da perna ativa (ida e par nas fases do RT; a perna
   *  ativa nos outros modos). Quem decide o que cada uma significa é o
   *  `FlightExplorer`. */
  onPickFlight: (offer: FlightExplorerOffer) => void;
  onSelectFares: SelectFaresFn;
}>) {
  const env = useExplorerEnv();
  const leg = state.legs[state.activeLeg];
  // Congelado, `idle` é terminal (readOnly pula o IO de montagem): spinner aqui
  // seria um "Buscando…" eterno — rende o vazio congelado.
  if (leg?.phase === 'idle' && frozen) {
    return (
      <div className="fex-empty">
        <p>{env.copy.page.frozenEmpty}</p>
      </div>
    );
  }
  if (searchingLeg(state)) {
    // A espera é o estado mais longo desta tela (p90 de 52s no `POST
    // /flight/search`): o esqueleto sozinho cala, e a partir de
    // `LONG_SEARCH_MS` ele passa a dizer que a busca segue em pé. Chave de
    // reset = perna ativa + seq: cada busca nova recomeça a contagem.
    return (
      <ExplorerSkeleton searchKey={`${String(state.activeLeg)}:${String(activeSeq(state))}`} />
    );
  }
  if (leg?.phase === 'error') {
    return (
      <ExplorerError
        state={state}
        frozen={frozen}
        onRetry={onRetry}
        {...(onNewSearch ? { onNewSearch } : {})}
        onSearch={onSearch}
      />
    );
  }
  if (rows.length === 0) {
    return (
      <ExplorerEmpty
        state={state}
        frozen={frozen}
        refining={refining}
        searchCeiling={searchCeiling}
        onRetry={onRetry}
        {...(onNewSearch ? { onNewSearch } : {})}
      />
    );
  }
  // A MESMA lista em modo SELEÇÃO em toda fase de lista — a linha escolhe o
  // VOO, sem escada (o host desliga o `fares.enabled` fora da fase de tarifa).
  // No RT a escada da fase 1 rodava na captura ONE-WAY da ida, com preços que
  // não são os do par; em todo modo a escolha de tarifa é a tela "Escolha as
  // tarifas" desde a padronização de 30/09 (#3875). O rótulo da lista é a
  // pergunta da fase, quando ela tem uma (as duas do RT).
  return (
    <OfferListBody
      env={offerListEnvOf(env)}
      offers={withRecommendedWhy(rows, env.copy.page.why)}
      fares={fares}
      readOnly={frozen}
      variant="results"
      // As linhas das fases do RT precificam o PAR (célula da matriz por perna)
      // sem carregar `returnLeg` — quem sabe a forma da viagem é o request.
      pairPricing={state.request.mode === 'round_trip'}
      onSelectFares={onSelectFares}
      // Stale da escada (#2332): a recuperação é o MESMO gesto do "Tentar de
      // novo" da fase — re-rodar a busca perdida, que recria os handles.
      {...(frozen ? {} : { onResearch: onRetry })}
      onSelectOffer={onPickFlight}
      {...(state.request.mode === 'round_trip'
        ? {
            listLabel:
              state.activeLeg === 0 ? env.copy.page.chooseOutbound : env.copy.page.chooseReturn,
          }
        : {})}
      {...(pin ? { pin } : {})}
      {...rowPropsOf(state, rows, env.airportSwitchOf)}
    />
  );
}

/**
 * Um slot de busca do host montado como COMPONENTE próprio, nunca chamado como
 * função no render da página: o slot pode usar hooks, e o planner da pill monta
 * e desmonta com o gesto — chamado direto, ele mudaria a contagem de hooks de
 * quem o chama. É também o que dá ao `ColumnBand` uma chave para remontar o
 * formulário do host.
 */
function SlotHost<P>({ render, props }: Readonly<{ render: (props: P) => ReactNode; props: P }>) {
  return render(props);
}

/** A construção do nó `band` da coluna — fora da página pelo teto de linhas
 *  dela. A FAIXA re-forma o pedido (ver a nota no `onSearch`); a chave por perna
 *  remonta o form quando o passo troca. */
export function ColumnBand({
  state,
  bandLeg,
  slot,
  frozen,
  io,
  onDirtyChange,
}: Readonly<{
  state: ExplorerState;
  bandLeg: number;
  slot: FlightExplorerBandSlot | undefined;
  frozen: boolean;
  io: ReturnType<typeof useExplorerIo>;
  onDirtyChange: (dirty: boolean) => void;
}>): ReactNode {
  // A faixa que DESMONTA leva o rascunho junto: a tela de tarifas a substitui
  // na coluna, e quando a lista volta o form renasce limpo do pedido corrente —
  // uma trava que sobrevivesse a isso prenderia o CTA atrás de uma edição que
  // ninguém vê mais. `onDirtyChange` é o setState do explorer (estável).
  useEffect(
    () => () => {
      onDirtyChange(false);
    },
    [onDirtyChange],
  );
  if (!slot) return null;
  const params = legSearchParams(state.request, bandLeg);
  return (
    <SlotHost
      render={slot}
      // A rota entra na chave: um pedido re-formado FORA da faixa (o atalho de
      // aeroporto do prazo estourado, #4465) remonta o form com a rota nova.
      key={`${String(bandLeg)}:${params.origin}>${params.destination}`}
      props={{
        params,
        ...(state.request.mode === 'multi_leg' ? { legs: state.request.legs } : {}),
        busy: explorerBusy(state),
        readOnly: frozen,
        // A FAIXA é o formulário do pedido, então ela RE-FORMA. Aba binária ⇒ o
        // pedido vira de uma perna; aba multi ⇒ o roteiro inteiro é refeito e o
        // fan-out renasce. A coluna não re-forma nada: os cortes refinam a busca
        // em cena, e a cabine refaz a busca mexendo só na cabine.
        onSearch: (p) => {
          io.runSearch(p);
        },
        onEditLegs: io.runLegsEdited,
        onDirtyChange,
      }}
    />
  );
}

/** A pill no lugar do título da barra (o slot `pill`); congelada vira leitura
 *  (segmentos sem botão) e o planner não tem por onde abrir. A lupa é o MESMO
 *  `io.retry` do "Tentar de novo" da coluna: re-busca a fase corrente sem
 *  passar pelo planner. */
export function pillNodeFor({
  slot,
  state,
  pillTarget,
  frozen,
  onExpand,
  onSearch,
}: Readonly<{
  slot: FlightExplorerPillSlot | undefined;
  state: ExplorerState;
  pillTarget: SummaryPillTarget | null;
  frozen: boolean;
  onExpand: (target: SummaryPillTarget) => void;
  onSearch: () => void;
}>): ReactNode {
  if (!slot) return undefined;
  return (
    <SlotHost
      render={slot}
      props={{
        request: state.request,
        hidden: pillTarget !== null,
        readOnly: frozen,
        onExpand,
        onSearch,
      }}
    />
  );
}

/** O planner da pill MONTADO — null fora do gesto ou sem o slot. */
function PillPlannerLayer({
  slot,
  target,
  state,
  bandLeg,
  io,
  onClose,
}: Readonly<{
  slot: FlightExplorerPlannerSlot | undefined;
  target: SummaryPillTarget | null;
  state: ExplorerState;
  bandLeg: number;
  io: ReturnType<typeof useExplorerIo>;
  onClose: () => void;
}>): ReactNode {
  if (target === null || !slot) return null;
  return (
    <SlotHost
      render={slot}
      props={{
        params: legSearchParams(state.request, bandLeg),
        ...(state.request.mode === 'multi_leg' ? { legs: state.request.legs } : {}),
        target,
        onClose,
        // Os MESMOS sinks da banda: re-buscar re-forma o pedido; editar o roteiro
        // refaz o fan-out.
        onSearch: (p) => {
          io.runSearch(p);
        },
        onEditLegs: io.runLegsEdited,
      }}
    />
  );
}
